import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError, setAuth, loadSession } from '../lib/api.js';
import { Button } from '../components/ui/index.js';
import { Icon } from '../lib/icons.js';
import { Ambient } from '../shell/Shell.js';

/** The password typed at login, kept in memory only, so a forced change doesn't ask for it twice. */
let justTyped: string | null = null;

function AuthFrame({ title, sub, children }: { title: string; sub?: ReactNode; children: ReactNode }): ReactNode {
  return (
    <div className="auth">
      <Ambient />
      <div className="auth-card glass glass-raised">
        <img className="auth-mark" src="/favicon.svg" alt="" width={52} height={52} />
        <h1>{title}</h1>
        {sub && <p className="auth-sub">{sub}</p>}
        {children}
      </div>
    </div>
  );
}

export function LoginScreen(): ReactNode {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!username.trim() || !password) return;
    setBusy(true);
    setError(null);
    try {
      const user = await api.login(username.trim().toLowerCase(), password);
      justTyped = password;
      setAuth({ status: 'authed', user });
      const next = params.get('next') ?? '/';
      // Server-rendered pages (iOS install / device registration) live outside the SPA.
      if (/^\/(?:api\/)?ios(?:\/|$)/.test(next)) { window.location.assign(next); return; }
      navigate(next, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Sign-in failed. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthFrame title="Sign in" sub="Your homes, in one place.">
      <form className="auth-form" onSubmit={(e) => void submit(e)}>
        <label className="field-label" htmlFor="username">
          Username
        </label>
        <input id="username" className="field" autoComplete="username" autoCapitalize="none" spellCheck={false} value={username} onChange={(e) => setUsername(e.target.value)} required />
        <label className="field-label" htmlFor="password">
          Password
        </label>
        <input id="password" className="field" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        {error && (
          <p className="form-error" role="alert">
            <Icon name="alert" size={15} /> {error}
          </p>
        )}
        <Button type="submit" variant="primary" block busy={busy} disabled={busy}>
          Sign in
        </Button>
      </form>
      <Link className="link-btn" to="/redeem">
        <Icon name="key" size={15} /> I have an invite code
      </Link>
      <Link className="link-btn" to="/pair">
        <Icon name="tablet" size={15} /> Pair a wall tablet
      </Link>
    </AuthFrame>
  );
}

const normaliseCode = (raw: string): string => {
  const c = raw.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);
  return c.length > 4 ? `${c.slice(0, 4)}-${c.slice(4)}` : c;
};

/** The code from the link: `#code=` (current links; never sent to the server) or `?code=` (old QR codes). */
const linkCode = (params: URLSearchParams): string | null =>
  new URLSearchParams(window.location.hash.replace(/^#/, '')).get('code') ?? params.get('code');

export function RedeemScreen(): ReactNode {
  const [params] = useSearchParams();
  const [initial] = useState(() => linkCode(params));
  const [code, setCode] = useState(() => normaliseCode(initial ?? ''));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const redeem = async (c: string): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const user = await api.redeem(c);
      setAuth({ status: 'authed', user });
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "That code didn't work.");
    } finally {
      setBusy(false);
    }
  };

  // A link with #code= (or an old ?code=) redeems straight away; the code is wiped from the address bar
  // and history first so it isn't left in the browser or picked up by anything reading the URL.
  useEffect(() => {
    if (initial) history.replaceState(history.state, '', '/redeem');
    if (initial && normaliseCode(initial).length === 9) void redeem(normaliseCode(initial));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthFrame title="Enter your code" sub="Your host sent you an eight-character code.">
      <form
        className="auth-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (code.length === 9) void redeem(code);
        }}
      >
        <input
          className="field code-field"
          aria-label="Invite code"
          placeholder="XXXX-XXXX"
          autoCapitalize="characters"
          autoComplete="one-time-code"
          spellCheck={false}
          inputMode="text"
          value={code}
          onChange={(e) => setCode(normaliseCode(e.target.value))}
        />
        {error && (
          <p className="form-error" role="alert">
            <Icon name="alert" size={15} /> {error}
          </p>
        )}
        <Button type="submit" variant="primary" block busy={busy} disabled={busy || code.length !== 9}>
          Continue
        </Button>
      </form>
      <Link className="link-btn" to="/login">
        <Icon name="user" size={15} /> Sign in with a password
      </Link>
    </AuthFrame>
  );
}

export function PasswordForm({ forced, onDone }: { forced?: boolean; onDone: () => void }): ReactNode {
  const [current, setCurrent] = useState(forced && justTyped ? justTyped : '');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mismatch = confirm.length > 0 && next !== confirm;
  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (next.length < 8) return setError('Use at least 8 characters.');
    if (next !== confirm) return setError("The two new passwords don't match.");
    if (next === current) return setError('Choose a password different from the current one.');
    setBusy(true);
    setError(null);
    try {
      await api.changePassword(current, next);
      justTyped = null;
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change the password.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="auth-form" onSubmit={(e) => void submit(e)}>
      {!(forced && justTyped) && (
        <>
          <label className="field-label" htmlFor="pw-current">
            Current password
          </label>
          <input id="pw-current" className="field" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required />
        </>
      )}
      <label className="field-label" htmlFor="pw-next">
        New password
      </label>
      <input id="pw-next" className="field" type="password" autoComplete="new-password" minLength={8} value={next} onChange={(e) => setNext(e.target.value)} required />
      <label className="field-label" htmlFor="pw-confirm">
        Repeat new password
      </label>
      <input id="pw-confirm" className="field" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} aria-invalid={mismatch} required />
      {error && (
        <p className="form-error" role="alert">
          <Icon name="alert" size={15} /> {error}
        </p>
      )}
      <Button type="submit" variant="primary" block busy={busy} disabled={busy}>
        {forced ? 'Set password and continue' : 'Change password'}
      </Button>
    </form>
  );
}

export function ForcedPasswordScreen(): ReactNode {
  return (
    <AuthFrame title="Choose a new password" sub="Your account was set up with a temporary password. Pick your own to continue.">
      <PasswordForm forced onDone={() => void loadSession()} />
      <button
        type="button"
        className="link-btn"
        onClick={() => {
          void api.logout().finally(() => setAuth({ status: 'anon' }));
        }}
      >
        <Icon name="logout" size={15} /> Sign out
      </button>
    </AuthFrame>
  );
}

export function AccessEndedScreen({ guestName }: { guestName?: string | undefined } = {}): ReactNode {
  return (
    <AuthFrame
      title={guestName ? 'Your stay has ended' : 'Your access has ended'}
      sub={guestName ? `Thanks for staying, ${guestName}. Your code no longer works; your host can send a new one.` : "This account no longer has access to any home. If you think that's a mistake, contact your host."}
    >
      <Button
        variant="primary"
        block
        onClick={() => {
          void api.logout().finally(() => {
            setAuth({ status: 'anon' });
            location.href = '/login';
          });
        }}
      >
        Back to sign in
      </Button>
    </AuthFrame>
  );
}

export function OfflineScreen({ onRetry }: { onRetry: () => void }): ReactNode {
  return (
    <AuthFrame title="Can't reach your homes" sub="Check your connection. The app will try again when you're back online.">
      <Button variant="primary" block icon="refresh" onClick={onRetry}>
        Try again
      </Button>
    </AuthFrame>
  );
}

export function Splash(): ReactNode {
  return (
    <div className="auth">
      <Ambient />
      <img className="splash-mark" src="/favicon.svg" alt="" width={56} height={56} />
    </div>
  );
}
